// US-PLATFORM-04 / REQ-9.7 — log redaction.
//
// Mirrors `src-tauri/src/security/redact.rs`. Same key list, same
// behaviour. Use redactForLog() before passing any object to a
// console.* call or any other logger.

/**
 * Any JSON key matching (case-insensitive) one of these is replaced
 * with the string "<redacted>" when passing through redactForLog.
 *
 * Keep in sync with the Rust REDACTED_KEYS list.
 */
export const REDACTED_KEYS: readonly string[] = [
  'authorization',
  'bearer',
  'cookie',
  'password',
  'password_hash',
  'password_params_json',
  'password_salt',
  'phc_hash',
  'secret',
  'session_id',
  'sessionid',
  'token',
];

const REDACTED_KEY_SET = new Set(REDACTED_KEYS);

/**
 * Return a deep copy of `value` with any field whose key matches
 * REDACTED_KEYS (case-insensitively) replaced by "<redacted>". Recurses
 * into nested objects and arrays.
 *
 * Does not mutate the input — the caller still has the original value.
 */
export function redactForLog(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(redactForLog);
  }
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (REDACTED_KEY_SET.has(k.toLowerCase())) {
        out[k] = '<redacted>';
      } else {
        out[k] = redactForLog(v);
      }
    }
    return out;
  }
  return value;
}

/**
 * Drop-in replacement for console.info that redacts every argument.
 * Prefer this over raw console.info anywhere structured payloads are
 * being logged.
 */
export function safeLog(level: 'info' | 'warn' | 'error', ...args: unknown[]): void {
  const redacted = args.map(redactForLog);
  // eslint-disable-next-line no-console
  console[level](...redacted);
}
